import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Search, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const RawMaterials = ({ user }) => {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    moq: '',
    max_qty: '',
    reorder_level: '',
    unit: '',
    location: '',
  });

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = materials.filter(m =>
        m.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMaterials(filtered);
    } else {
      setFilteredMaterials(materials);
    }
    setCurrentPage(1);
  }, [searchTerm, materials]);

  const paginatedMaterials = filteredMaterials.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const fetchMaterials = async () => {
    try {
      const [materialsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/raw-materials`),
        axios.get(`${API}/warehouse-locations`)
      ]);
      setMaterials(materialsRes.data);
      setFilteredMaterials(materialsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/raw-materials/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Upload complete: ${res.data.created} created, ${res.data.updated} updated`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} errors occurred`);
      }
      setUploadDialogOpen(false);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['code', 'name', 'unit', 'moq', 'max_order_quantity', 'reorder_level', 'location', 'current_stock'];
    const sampleData = ['RM001', 'Sample Material', 'kg', '100', '1000', '200', 'Warehouse A', '500'];
    const csvContent = [headers.join(','), sampleData.join(',')].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'raw_materials_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      moq: '',
      max_qty: '',
      reorder_level: '',
      unit: '',
      location: '',
    });
    setEditingMaterial(null);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      code: material.code,
      name: material.name,
      moq: material.moq,
      max_qty: material.max_qty,
      reorder_level: material.reorder_level,
      unit: material.unit,
      location: material.location || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        moq: parseFloat(formData.moq),
        max_qty: parseFloat(formData.max_qty),
        reorder_level: parseFloat(formData.reorder_level),
        current_stock: 0,
      };

      if (editingMaterial) {
        await axios.put(`${API}/raw-materials/${editingMaterial.id}`, data);
        toast.success('Material updated successfully');
      } else {
        await axios.post(`${API}/raw-materials`, data);
        toast.success('Material created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const handleDeleteClick = (material) => {
    setItemToDelete(material);
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/raw-materials/${itemToDelete.id}`);
      toast.success('Material deleted successfully');
      fetchMaterials();
      setConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete material');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse mb-2"></div>
        <div className="h-4 w-64 bg-stone-200/60 rounded animate-pulse mb-8"></div>
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm">
          <TableSkeleton rows={5} columns={7} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="raw-materials-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Raw Materials</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Manage your raw material inventory</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-materials-btn">
                <Upload size={16} className="mr-2" />
                Upload Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Raw Materials from Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-sm text-sm">
                  <p className="font-medium mb-2">Required Columns:</p>
                  <ul className="text-xs text-stone-600 space-y-1">
                    <li>• <strong>code</strong> - Material code (required)</li>
                    <li>• <strong>name</strong> - Material name (required)</li>
                    <li>• <strong>unit</strong> - Unit of measure (default: pcs)</li>
                    <li>• <strong>moq</strong> - Minimum order quantity</li>
                    <li>• <strong>max_order_quantity</strong> - Maximum order quantity</li>
                    <li>• <strong>reorder_level</strong> - Reorder level</li>
                    <li>• <strong>location</strong> - Warehouse location</li>
                    <li>• <strong>current_stock</strong> - Current stock quantity</li>
                  </ul>
                </div>

                <Button variant="outline" size="sm" onClick={downloadTemplate} className="w-full">
                  <Download size={14} className="mr-2" /> Download Template
                </Button>

                <div>
                  <Label htmlFor="file-upload">Select Excel File</Label>
                  <Input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="mt-1"
                  />
                </div>

                {uploading && <p className="text-sm text-center text-stone-500">Uploading...</p>}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="add-material-button">
                <Plus size={16} className="mr-2" />
                Add Material
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingMaterial ? 'Edit Material' : 'Add New Material'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="material-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="code">Material Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      required
                      data-testid="input-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Material Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="input-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="moq">MOQ</Label>
                    <Input
                      id="moq"
                      type="number"
                      step="0.01"
                      value={formData.moq}
                      onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                      required
                      data-testid="input-moq"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_qty">Max Quantity</Label>
                    <Input
                      id="max_qty"
                      type="number"
                      step="0.01"
                      value={formData.max_qty}
                      onChange={(e) => setFormData({ ...formData, max_qty: e.target.value })}
                      required
                      data-testid="input-max-qty"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reorder_level">Reorder Level</Label>
                    <Input
                      id="reorder_level"
                      type="number"
                      step="0.01"
                      value={formData.reorder_level}
                      onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                      required
                      data-testid="input-reorder-level"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="unit">Unit</Label>
                  <Input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="kg, ltr, pcs"
                    required
                    data-testid="input-unit"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location/Warehouse</Label>
                  <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="submit-material-button">
                    {editingMaterial ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-materials"
          />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden mb-4">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="materials-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">MOQ</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Max Qty</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Reorder Level</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Unit</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {paginatedMaterials.map((material) => (
                <tr key={material.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`material-row-${material.code}`}>
                  <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{material.code}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{material.name}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{material.moq}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{material.max_qty}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{material.reorder_level}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{material.unit}</td>
                  <td className="px-6 py-2 text-sm">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(material)}
                        data-testid={`edit-material-${material.code}`}
                      >
                        <Edit size={14} />
                      </Button>
                      {user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(material.id)}
                          data-testid={`delete-material-${material.code}`}
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {paginatedMaterials.map((material) => (
            <div key={`mobile-${material.id}`} className="p-4 bg-white">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-[hsl(145,60%,10%)]">{material.name}</h4>
                  <p className="text-xs text-[hsl(145,20%,40%)]">{material.code}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(material)} className="h-8 w-8 p-0 text-stone-400 hover:text-[hsl(145,60%,30%)] hover:bg-[hsl(145,60%,95%)]" data-testid={`edit-material-${material.code}`}>
                    <Edit size={14} />
                  </Button>
                  {user?.role === 'admin' && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(material)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-material-${material.code}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                <div>
                  <span className="text-xs text-stone-500 block">Unit</span>
                  <span className="font-medium">{material.unit}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-500 block">Reorder Lvl</span>
                  <span className="font-medium">{material.reorder_level}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-500 block">MOQ</span>
                  <span className="font-medium">{material.moq}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-500 block">Max Qty</span>
                  <span className="font-medium">{material.max_qty}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center">
        <Pagination currentPage={currentPage} totalItems={filteredMaterials.length} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={executeDelete}
        title={`Delete Material: ${itemToDelete?.name}?`}
        description="This action cannot be undone. This material will be removed from your catalog."
        confirmText="Delete Material"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default RawMaterials;